import React, { useState, useRef, useEffect } from "react";
import { StyleSheet, View, Text, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { BrandColors } from "@/constants/ThemeColours";
import { resolveVideoMimeType } from "common247";

/**
 * Props for the VideoPlayer component.
 */
export interface IVideoPlayerProps {
  /** Source URL of the video file */
  url: string;
  /** MIME content type of the video (e.g. video/mp4, video/webm) */
  contentType?: string;
  /** Original file name or display name of the video */
  fileName?: string;
  name?: string;
  /** Container style overrides */
  style?: any;
  /** How the video content should fit within the container */
  resizeMode?: "contain" | "cover";
}

/**
 * Robust web video player component using HTML5 video.
 * Isolates mouse/pointer/touch events from React Native Web's ScrollResponder to prevent
 * native video controls and play buttons from being cancelled or swallowed.
 * Streams cross-origin video files without CORS blocks (removes restrictive crossOrigin attribute),
 * supports fallback open and download actions, and resolves proper video MIME types.
 *
 * @param {IVideoPlayerProps} props - The component props.
 * @returns {React.ReactElement} The rendered video player.
 */
export function VideoPlayer({
  url,
  contentType,
  fileName,
  name,
  style,
  resizeMode = "contain",
}: IVideoPlayerProps): React.ReactElement {
  const cleanUrl = (url || "").trim();
  const displayName = name || fileName;

  if (!cleanUrl) {
    return (
      <View style={[style, styles.errorContainer]}>
        <Ionicons name="videocam-off-outline" size={48} color="#FF6B6B" style={{ marginBottom: 10 }} />
        <Text style={styles.errorTitle}>Unable to play video</Text>
        <Text style={styles.errorSubtitle}>No valid video stream URL was provided.</Text>
        {displayName ? <Text style={styles.errorFileName}>{displayName}</Text> : null}
      </View>
    );
  }

  const sanitizedUrl = cleanUrl.includes(" ") ? encodeURI(cleanUrl) : cleanUrl;
  const resolvedMimeType = resolveVideoMimeType(contentType, displayName, sanitizedUrl);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    setHasError(false);
    setIsPlaying(false);
    setErrorCode(null);
    setErrorMessage(null);
  }, [sanitizedUrl]);

  /**
   * Toggles playback between play and pause while stopping event bubbling.
   *
   * @param {any} [e] - Optional click or pointer event.
   */
  const handleTogglePlay = (e?: any) => {
    if (e) {
      if (typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }
      if (typeof e.preventDefault === "function") {
        e.preventDefault();
      }
    }
    const video = videoRef.current;
    if (!video) return;

    if (video.paused || video.ended) {
      video.play().catch((err: any) => {
        console.warn("Video playback play() error:", err);
      });
    } else {
      video.pause();
    }
  };

  /**
   * Captures media error details when video fails to load or decode.
   *
   * @param {any} e - Media error event.
   */
  const handleVideoError = (e: any) => {
    const video = videoRef.current;
    const mediaError = video?.error;
    let codeDesc = "Unknown error";
    if (mediaError) {
      switch (mediaError.code) {
        case 1:
          codeDesc = "Playback aborted";
          break;
        case 2:
          codeDesc = "Network error loading video stream";
          break;
        case 3:
          codeDesc = "Error decoding video codec or format";
          break;
        case 4:
          codeDesc = "Video format or codec not supported by browser";
          break;
      }
    }
    console.warn("Video playback error in VideoPlayer.web:", {
      code: mediaError?.code,
      codeDesc,
      message: mediaError?.message,
      url: sanitizedUrl,
      fileName,
      resolvedMimeType,
      rawEvent: e,
    });
    setHasError(true);
    setErrorCode(mediaError?.code ?? null);
    setErrorMessage(mediaError?.message || null);
  };

  /**
   * Retries loading the video after an error.
   *
   * @param {any} [e] - Optional click event.
   */
  const handleRetry = (e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    setHasError(false);
    setErrorCode(null);
    setErrorMessage(null);
    const video = videoRef.current;
    if (video) {
      video.load();
      video.play().catch((err: any) => console.warn("Retry play error:", err));
    }
  };

  /**
   * Opens the video stream directly in a new browser tab.
   *
   * @param {any} [e] - Optional click event.
   */
  const handleOpenInNewTab = (e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (typeof window !== "undefined" && sanitizedUrl) {
      window.open(sanitizedUrl, "_blank", "noopener,noreferrer");
    }
  };

  /**
   * Triggers a direct download of the video file.
   *
   * @param {any} [e] - Optional click event.
   */
  const handleDownload = (e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (typeof document !== "undefined" && sanitizedUrl) {
      const a = document.createElement("a");
      a.href = sanitizedUrl;
      a.download = fileName || "video.mp4";
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  /**
   * Formats a user-friendly error explanation based on MediaError code.
   *
   * @param {number | null} code - The MediaError code.
   * @param {string | null} _msg - The raw message.
   * @returns {string} Explanatory subtitle string.
   */
  const getFriendlyErrorMessage = (code: number | null, _msg: string | null): string => {
    if (code === 2) {
      return "A network error occurred while loading the video stream. Please check your connection.";
    }
    if (code === 3 || code === 4) {
      return "The video codec or format (e.g. HEVC/H.265) is not supported by your browser. You can open it in a new tab or download the file to view.";
    }
    return "The video stream could not be loaded or is in an unsupported format.";
  };

  return (
    <View
      style={[
        style,
        styles.container,
        {
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          overflow: "hidden",
          position: "relative",
        },
      ]}
    >
      {/* 
        Native HTML5 video element wrapped in an event-isolating container.
        Stopping propagation on pointer/mouse/touch events ensures React Native Web's
        ScrollView / ResponderSystem does not invoke preventDefault() on native play controls.
      */}
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <video
          ref={videoRef}
          src={sanitizedUrl}
          controls
          playsInline
          preload="metadata"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onError={handleVideoError}
          style={{
            width: "100%",
            height: "100%",
            maxHeight: "100%",
            objectFit: resizeMode === "contain" ? "contain" : "cover",
            borderRadius: 12,
            outline: "none",
            backgroundColor: "#000",
            pointerEvents: "auto",
            display: hasError ? "none" : "block",
          }}
        >
          Your browser does not support the video tag.
        </video>

        {/* Central Overlay Play Button when video is paused and ready */}
        {!hasError && !isPlaying && (
          <button
            type="button"
            onClick={handleTogglePlay}
            aria-label="Play video"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 68,
              height: 68,
              borderRadius: "50%",
              backgroundColor: "rgba(0, 0, 0, 0.65)",
              border: "2px solid rgba(255, 255, 255, 0.9)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
              transition: "transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.2s ease",
              zIndex: 20,
              paddingLeft: 4, // Slight offset for optical play icon centering
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translate(-50%, -50%) scale(1.08)";
              e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.85)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translate(-50%, -50%) scale(1)";
              e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.65)";
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="#FFFFFF">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        )}

        {/* Error Overlay with retry, open in new tab, and download buttons */}
        {hasError && (
          <View style={styles.errorContainer}>
            <Ionicons name="alert-circle-outline" size={54} color="#FF6B6B" style={{ marginBottom: 10 }} />
            <Text style={styles.errorTitle}>Unable to play video</Text>
            <Text style={styles.errorSubtitle}>{getFriendlyErrorMessage(errorCode, errorMessage)}</Text>
            {fileName ? <Text style={styles.errorFileName}>{fileName}</Text> : null}
            <View style={styles.errorActionsRow}>
              <TouchableOpacity style={styles.retryButton} onPress={handleRetry} activeOpacity={0.8}>
                <Ionicons name="refresh" size={16} color="#fff" style={{ marginRight: 6 }} />
                <Text style={styles.retryButtonText}>Retry Playback</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.secondaryButton} onPress={handleOpenInNewTab} activeOpacity={0.8}>
                <Ionicons name="open-outline" size={16} color="#fff" style={{ marginRight: 6 }} />
                <Text style={styles.secondaryButtonText}>Open in New Tab</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.secondaryButton} onPress={handleDownload} activeOpacity={0.8}>
                <Ionicons name="download-outline" size={16} color="#fff" style={{ marginRight: 6 }} />
                <Text style={styles.secondaryButtonText}>Download</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </div>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
  },
  errorContainer: {
    backgroundColor: "#1A1A1A",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    borderRadius: 12,
    width: "100%",
    height: "100%",
  },
  errorTitle: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 6,
  },
  errorSubtitle: {
    color: "rgba(255, 255, 255, 0.65)",
    fontSize: 12,
    textAlign: "center",
    marginBottom: 16,
    maxWidth: 340,
    lineHeight: 18,
  },
  errorFileName: {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 11,
    textAlign: "center",
    marginBottom: 14,
  },
  errorActionsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
  },
  retryButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: BrandColors.sungoldOrange.hex,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  retryButtonText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
  secondaryButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.2)",
  },
  secondaryButtonText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
});
